/**
 * Átomos visuais da gestão de usuários — os mesmos no desktop e no mobile, para
 * que perfil, eixo e status tenham UMA cara no sistema inteiro.
 */
import { EixoChip as ChipEixo } from "@caint/design-system";
import {
  EIXOS,
  PAPEL_GRUPO,
  type StatusUsuario,
  destacar,
  indiceCor,
  iniciais,
  rotuloPapel,
  rotuloPapelCurto,
} from "../../lib/usuariosLista.ts";
import "../../styles/usuarios.css";

const TONS_AVATAR = 6;

/** Círculo com as iniciais. A cor sai do `id`, então é a mesma em toda tela. */
export function Avatar({
  nome,
  id,
  inativo,
  tamanho = 36,
}: {
  nome: string;
  id: string;
  inativo?: boolean;
  tamanho?: number;
}) {
  const tom = inativo ? "off" : indiceCor(id, TONS_AVATAR);
  return (
    <span
      className={`us-avatar us-av-${tom}`}
      style={{ width: tamanho, height: tamanho, fontSize: Math.round(tamanho * 0.36) }}
      aria-hidden="true"
    >
      {iniciais(nome)}
    </span>
  );
}

/** Selo do perfil, colorido pela família (avaliação, supervisão, gestão…). */
export function PerfilBadge({ papel }: { papel: string }) {
  return (
    <span className={`us-perfil us-perfil-${PAPEL_GRUPO[papel] ?? "leitura"}`} title={rotuloPapel(papel)}>
      {rotuloPapelCurto(papel)}
    </span>
  );
}

/** Chip do eixo (o do design-system), tolerando conta sem eixo. */
export function EixoChip({ eixo }: { eixo: string | null }) {
  if (!eixo) {
    return (
      <span className="us-vazio">
        <span aria-hidden="true">—</span>
        <span className="us-so-leitor">Sem eixo</span>
      </span>
    );
  }
  const conhecido = EIXOS.find((x) => x === eixo);
  return conhecido ? <ChipEixo eixo={conhecido} /> : <span className="chip chip-eixo chip-des">{eixo}</span>;
}

const STATUS_ROTULO: Record<StatusUsuario, string> = {
  ativo: "Ativo",
  inativo: "Inativo",
  pendente: "Acesso pendente",
};

const STATUS_DICA: Record<StatusUsuario, string> = {
  ativo: "Conta ativa",
  inativo: "Conta desativada — não consegue entrar",
  pendente: "Conta ativa, mas a pessoa ainda não entrou com a senha provisória",
};

export function StatusPill({ status }: { status: StatusUsuario }) {
  return (
    <span className={`us-status us-status-${status}`} title={STATUS_DICA[status]}>
      <span className="us-status-ponto" aria-hidden="true" />
      {STATUS_ROTULO[status]}
    </span>
  );
}

/** Texto com os termos da busca realçados (sem acento/caixa). */
export function Destaque({ texto, busca }: { texto: string; busca: string }) {
  if (!busca.trim()) {
    return <>{texto}</>;
  }
  const trechos = destacar(texto, busca);
  let pos = 0;
  return (
    <>
      {trechos.map((t) => {
        const chave = pos;
        pos += t.texto.length;
        return t.marca ? (
          <mark key={chave} className="us-mark">
            {t.texto}
          </mark>
        ) : (
          <span key={chave}>{t.texto}</span>
        );
      })}
    </>
  );
}
